/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userListsWithBoxes.config.json";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import {
  GET_MY_COMMITTE_INVITE_STATUS,
  GET_MY_PROFILE,
  GET_PRESENTATIONS,
  GET_USERS_WITH_BOXES,
} from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IUserListsWithBoxesMethods extends IPluginMethodMap {
  getUserListsWithBoxesProps(args: any): Promise<any>;
}

export class UserListsWithBoxesService implements IAgentPlugin {
  readonly methods: IUserListsWithBoxesMethods;

  constructor() {
    this.methods = {
      getUserListsWithBoxesProps: this.getUserListsWithBoxesProps.bind(this),
    };
  }

  async getUserListsWithBoxesProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let filter = {};
    console.log("args?.criteria?.role==>", args);
    let searchType = this.determineSearchType(args?.criteria);
    console.log("whereCls search==>", searchType);
    if (searchType && searchType !== null) {
      if (searchType === "multi") {
        filter = this.getMultiFilter(args?.criteria?.data);
      } else {
        filter = this.convertCriteria(args?.criteria?.data);
      }
    }
    const _filter = {
      status: {
        eq: "active",
      },
      year: {
        eq: "2024",
      },
    };
    try {
      if (client) {
        const _res = await client.query({
          query: GET_MY_PROFILE,
          fetchPolicy: "network-only",
        });
        console.log("_res==>", _res);
        let comm_pre_id: any = "",
          presentationOptions: any = [],
          defaultPresentation: any = "";

        if (_res?.data?.getMyProfile?.role[0]?.name === "committeMember") {
          const comm_presentation = await client.query({
            query: GET_MY_COMMITTE_INVITE_STATUS,
            fetchPolicy: "network-only",
          });
          if (
            comm_presentation?.data?.getMyCommitteInviteStatus?.content[0]
              ?.presentation?.id
          ) {
            defaultPresentation =
              comm_presentation?.data?.getMyCommitteInviteStatus?.content[0]
                ?.presentation?.id;
          } else {
            throw new Error("No invites !!");
          }
        }

        if (_res?.data?.getMyProfile?.role[0]?.name !== "committeMember") {
          const presentation = await client.query({
            query: GET_PRESENTATIONS,
            variables: {
              filter: _filter,
              options: {
                limit: args?.size ?? 10,
                skip: args?.offset ?? 0,
              },
            },
            fetchPolicy: "network-only",
          });

          defaultPresentation =
            presentation?.data?.getPresentations?.content[0]?.id;
          presentationOptions =
            presentation?.data?.getPresentations?.content?.map((_item: any) => {
              return { value: _item?.id, label: _item?.name };
            });
        }
        console.log("id====>", args?.criteria?.presentation, "default==>", defaultPresentation)
        const checkPresentation = args?.criteria?.presentation ?? defaultPresentation;
        if (!checkPresentation) {
          throw new Error("No Active Presentations");
        }
        const res = await client.query({
          query: GET_USERS_WITH_BOXES,
          variables: {
            presentationId: args?.criteria?.presentation ?? defaultPresentation,
            invitedByUser: _res?.data?.getMyProfile?.id,
            filter: {
              ...filter,
              // role: {
              //   eq: "fe93dab0-424b-4aa3-9efa-c001b4c04444"
              // }
            },
            options: {
              limit: args?.size ?? 10,
              skip: args?.offset ?? 0,
              sort: {
                field: "createdAt",
                order: "desc"
              }
            },
          },
          fetchPolicy: "network-only",
        });

        if (res?.data) {
          console.log("id====>", args?.criteria?.presentation, "default==>", defaultPresentation)
          let _rowData = this.formatUserTable(
            res?.data?.getUsersWithBoxes?.content
          );

          let _totalPages = res?.data?.getUsersWithBoxes?.count
            ? parseInt(res?.data?.getUsersWithBoxes?.count) / 10
            : 1;

          let _currentPage =
            res?.data?.getUsersWithBoxes?.skip /
            res?.data?.getUsersWithBoxes?.limit +
            1;

          return {
            ...config,
            ultUsers: _rowData,
            isEmpty: res?.data?.getUsersWithBoxes?.data?.length <= 0,
            activePage: _currentPage,
            currentPage: _currentPage,
            pageSize: _rowData.length,
            totalCount: res?.data?.getUsersWithBoxes?.count,
            totalPages: _totalPages,
            searchPresentationFilter: presentationOptions,
            presentation: defaultPresentation,
            email: _res?.data?.getMyProfile?.email
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
  private determineSearchType(criteria: any) {
    if (Object.keys(criteria).length === 0) {
      return null;
    } else if (Object.keys(criteria?.data)[0] === "0") {
      return "multi";
    } else {
      return "normal";
    }
  }

  private convertCriteria(criteria: any) {
    const result: any = {};
    for (const key in criteria) {
      if (!(key === "inviteStatus" && criteria[key] === "all")) {
        result[key] = { contains: criteria[key] };
      }
    }
    return result;
  }

  private getMultiFilter(criteria: any) {
    const transformedCriteria: any = {};
    for (const key in criteria) {
      if (criteria.hasOwnProperty(key)) {
        const { key: filterKey, filters } = criteria[key];
        const { inputData } = filters;
        const filterType = Object.keys(filters).find(key => key !== 'inputData');

        const filterValue = filters[filterType];

        if (inputData.id === "Boxes___boxStatus") {
          // If the id is "Boxes___boxStatus", assign the value directly
          transformedCriteria[inputData.id] = filterValue;
        } else {
          // Convert "true" and "false" strings to actual boolean values
          if (typeof filterValue === 'string' && filterValue.toLowerCase() === 'true') {
            transformedCriteria[inputData.id] = {
              [filterType.toLowerCase().replace('equal', 'eq')]: true
            };
          } else if (typeof filterValue === 'string' && filterValue.toLowerCase() === 'false') {
            transformedCriteria[inputData.id] = {
              [filterType.toLowerCase().replace('equal', 'eq')]: false
            };
          } else {
            transformedCriteria[inputData.id] = {
              [filterType.toLowerCase().replace('equal', 'eq')]: filterValue
            };
          }
        }
      }
    }
    return transformedCriteria;
  }

  private formatUserTable(data: any) {
    let _rowData: any = [];
    Array.isArray(data) &&
      data.forEach((item: any) => {
        console.log("res?.data==>", pretty(item));
        _rowData.push({
          id: item?.id,
          vuid: item?.vuid,
          boxStatus: item?.Boxes[0]?.boxStatus ? this.getBoxStatus(item?.Boxes[0]?.boxStatus) : "Not Purchased",
          section: item?.Boxes[0]?.section?.name ?? "-",
          sectionOptType: item?.Referal?.sectionOptType ?? "-",
          partition:
            item?.Referal?.partition ??
            // item?.Boxes[0]?.Referal?.partition ??
            "-",
          inviteStatus: item?.inviteStatus ?? "-",
          amountPaid: item?.Boxes[0]?.amountPaid ? item?.Boxes[0]?.amountPaid.toFixed(2) : 0,
          discountAmount: item?.Boxes[0]?.discount?.discountAmount ?? 0,
          totalAmount: item?.Boxes[0]?.totalAmount ? item?.Boxes[0]?.totalAmount.toFixed(2) : 0,
          paymentStatus: item?.Boxes[0]?.paymentStats ?? "-",
          userStatus: this.getFormattedStatus(
            item?.accountStatus,
            item?.suspended
          ),
          name: `${item?.firstName ?? "Null"} ${item?.lastName ?? "Null"}`,
          email: item?.email,
          gender: item?.gender ?? "-",
          verified: item?.emailVerified,
          role: this._getRole(item?.role),
          inviteNote: item?.Referal?.inviteNote ?? "-",
          createdAt: item?.createdAt ? this.formatCreatedDate(item?.createdAt) : " "
        });
      });
    return _rowData;
  }

  private getBoxStatus(boxStatus: string) {
    if (boxStatus) {
      if (boxStatus == "PartialPurchased") {
        return "Partially Paid"
      } else if (boxStatus == "Purchased") {
        return "Paid"
      }
      return boxStatus;
    }
  }

  private _getRole(roles: any[]) {
    let _res: any[] = [];
    roles &&
      Array.isArray(roles) &&
      roles.length > 0 &&
      roles.map((item: any, index: number) => {
        _res.push(item?.name);
      });
    return _res;
  }

  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format("LLL");
    }
  }

  private getFormattedStatus(status: string, suspended: boolean) {
    if (suspended) {
      return "suspended";
    } else {
      switch (status) {
        case "USER_ADDED":
          return "Imported";
        case "USER_INVITED":
          return "Invited";
        case "USER_VERIFIED":
          return "Registered";
        case "USER_ACC_CREATED":
          return "Registered";
        case "SUSPENDED":
          return "Suspended";
        default:
          return "-";
      }
    }
  }

  private getFilter(criteria: any) {
    if (criteria?.firstName) {
      return {
        column: "firstName",
        op: "Contains",
        value: criteria?.firstName.toLowerCase(),
      };
    }
    if (criteria?.email) {
      return {
        column: "email",
        op: "Contains",
        value: criteria?.email.toLowerCase(),
      };
    }
    return null;
  }

}
